import React from 'react';

import './StartMenu.css';

import type { SaveOverview } from '../utils/saveSlots';

interface StartMenuProps {
  isOpen: boolean;
  saveOverview: SaveOverview;
  onStartNew: () => void;
  onContinue: () => void;
  onLoadDemo: () => void;
  onManageSaves?: () => void;
}

const formatTimestamp = (timestamp: number | null): string => {
  if (!timestamp) {
    return '';
  }

  try {
    return new Date(timestamp).toLocaleString();
  } catch {
    return '';
  }
};

export const StartMenu: React.FC<StartMenuProps> = ({
  isOpen,
  saveOverview,
  onStartNew,
  onContinue,
  onLoadDemo,
  onManageSaves,
}) => {
  const hasLoadableSave = saveOverview.hasAnySave;
  const latestLabel = saveOverview.latestSaveName;
  const latestTimestamp = formatTimestamp(saveOverview.latestSaveTimestamp);

  if (!isOpen) {
    return null;
  }

  const continueAction = hasLoadableSave ? onContinue : onLoadDemo;
  const continueTitle = hasLoadableSave ? 'Continue Game' : 'Play Demo Factory';
  const continueDescription = hasLoadableSave
    ? latestLabel
      ? `Resume "${latestLabel}" from where you left off.`
      : 'Pick up your last factory run right away.'
    : 'Jump into a ready-made megafactory to see automation in full swing.';
  const continueMeta = hasLoadableSave
    ? latestTimestamp
      ? `Last played ${latestTimestamp}`
      : 'Latest save ready to load.'
    : 'Great for learning advanced layouts and production lines.';

  return (
    <div className='start-menu-overlay'>
      <div className='start-menu'>
        <div className='start-menu__layout'>
          <img
            className='start-menu__hero-poster'
            src='/factorygpt-poster-scaled.png'
            srcSet='/factorygpt-poster-scaled@2x.png 2x'
            width={260}
            height={390}
            alt='FactoryGPT poster featuring the neon factory skyline'
            loading='lazy'
          />

          <div className='start-menu__content'>
            <h1 className='start-menu__title'>FactoryGPT</h1>

            <div className='start-menu__posters' role='list'>
              <button
                type='button'
                className='start-menu__poster start-menu__poster--primary'
                onClick={onStartNew}
              >
                <span className='start-menu__poster-tag'>Fresh Run</span>
                <h2 className='start-menu__poster-title'>Start New Game</h2>
                <p className='start-menu__poster-description'>
                  Land on a pristine world, harvest resources, and build your
                  empire from the ground up.
                </p>
                <span className='start-menu__poster-meta'>
                  Classic top-down and 3D modes supported.
                </span>
              </button>

              <button
                type='button'
                className='start-menu__poster start-menu__poster--secondary'
                onClick={continueAction}
              >
                <span className='start-menu__poster-tag'>
                  {hasLoadableSave ? 'Resume' : 'Showcase'}
                </span>
                <h2 className='start-menu__poster-title'>{continueTitle}</h2>
                <p className='start-menu__poster-description'>
                  {continueDescription}
                </p>
                <span className='start-menu__poster-meta'>{continueMeta}</span>
              </button>
            </div>

            <div className='start-menu__actions'>
              {onManageSaves ? (
                <button
                  type='button'
                  className='start-menu__action-button'
                  onClick={onManageSaves}
                >
                  Manage Saves
                </button>
              ) : null}
              <span className='start-menu__hint'>
                You can toggle between 2D and 3D modes anytime from the top bar.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
